import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Eye, EyeOff, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

/**
 * Two-Factor Authentication management component for BOH settings.
 * Supports TOTP via Google Authenticator (RFC 6238).
 */
export default function TwoFactorAuth({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [step, setStep] = useState('idle');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, [token]);

  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (r.ok) setStatus(await r.json());
    } catch {}
    setLoading(false);
  };

  const startSetup = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Setup failed');
      setSetupData(data);
      setStep('setup');
      setCode('');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const activateCode = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code from your app'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Activation failed');
      toast.success('2FA activated!');
      if (data.backup_codes) {
        setBackupCodes(data.backup_codes);
        setStep('backup');
      } else {
        setStep('idle');
      }
      setSetupData(null);
      setCode('');
      fetchStatus();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const disable2fa = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code to confirm'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/2fa/disable`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Failed to disable 2FA');
      toast.success('2FA has been disabled.');
      setStep('idle');
      setCode('');
      fetchStatus();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData?.secret || '');
    toast.success('Secret key copied to clipboard');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <Card data-testid="2fa-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {status?.enabled
            ? <><ShieldCheck className="h-5 w-5 text-green-600" /> Two-Factor Authentication</>
            : <><Shield className="h-5 w-5 text-gray-400" /> Two-Factor Authentication</>
          }
        </CardTitle>
        <CardDescription>
          {status?.enabled
            ? 'Your account is protected with Google Authenticator (TOTP).'
            : 'Add an extra layer of security with Google Authenticator.'
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Status banner */}
        <div className={`mb-4 flex items-center gap-3 p-3 rounded-xl ${
          status?.enabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'
        }`}>
          {status?.enabled
            ? <><ShieldCheck className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 text-sm">2FA Enabled</p>
                  <p className="text-xs text-green-600">Every login requires your authenticator code</p>
                </div></>
            : <><Shield className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700 text-sm">2FA Disabled</p>
                  <p className="text-xs text-gray-500">Enable for enhanced account security</p>
                </div></>
          }
        </div>

        <AnimatePresence mode="wait">
          {step === 'idle' && (
            <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {!status?.enabled ? (
                <Button onClick={startSetup} disabled={submitting} className="w-full" data-testid="setup-2fa-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Smartphone className="h-4 w-4 mr-2" />}
                  Set Up Google Authenticator
                </Button>
              ) : (
                <div className="space-y-3">
                  {status.backup_codes_remaining !== undefined && (
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Backup Recovery Codes</p>
                        <p className="text-xs text-gray-500">{status.backup_codes_remaining} codes remaining</p>
                      </div>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => { setStep('disable'); setCode(''); }} className="w-full text-red-600 border-red-200 hover:bg-red-50" data-testid="disable-2fa-btn">
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Disable 2FA
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {step === 'setup' && setupData && (
            <motion.div key="setup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-5">
              {/* Step 1: Scan QR */}
              <div>
                <p className="font-semibold text-sm text-gray-800 mb-1">Step 1: Scan QR Code</p>
                <p className="text-xs text-gray-500 mb-3">Open Google Authenticator → tap "+" → "Scan QR code"</p>
                <div className="flex justify-center p-4 bg-white border-2 border-gray-200 rounded-2xl">
                  <img src={setupData.qr_code} alt="2FA QR Code" className="w-44 h-44" data-testid="qr-code-img" />
                </div>
              </div>

              {/* Manual entry */}
              <div>
                <p className="font-semibold text-sm text-gray-800 mb-1">Can't scan? Enter key manually</p>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <code className="flex-1 font-mono text-xs text-gray-700 break-all">
                    {showSecret ? setupData.secret : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <button onClick={() => setShowSecret(s => !s)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={copySecret} className="text-primary hover:text-primary/80 flex-shrink-0">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Step 2: Verify */}
              <div>
                <p className="font-semibold text-sm text-gray-800 mb-1">Step 2: Enter the 6-digit code</p>
                <p className="text-xs text-gray-500 mb-2">Enter the code shown in Google Authenticator to confirm setup</p>
                <Input
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="font-mono text-2xl tracking-[0.5em] text-center h-14"
                  data-testid="2fa-code-input"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setStep('idle'); setSetupData(null); }} className="flex-1" disabled={submitting}>Cancel</Button>
                <Button onClick={activateCode} disabled={submitting || code.length !== 6} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="activate-2fa-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Activate 2FA
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'disable' && (
            <motion.div key="disable" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm font-medium text-red-800">Disabling 2FA reduces your account security.</p>
                <p className="text-xs text-red-600 mt-0.5">Enter your current authenticator code to confirm.</p>
              </div>
              <Input
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter authenticator code"
                maxLength={6}
                className="font-mono text-2xl tracking-[0.5em] text-center h-14"
                data-testid="disable-2fa-code-input"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setStep('idle'); setCode(''); }} className="flex-1" disabled={submitting}>Cancel</Button>
                <Button onClick={disable2fa} disabled={submitting || code.length !== 6} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-disable-2fa-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Disable 2FA
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'backup' && backupCodes && (
            <motion.div key="backup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm font-bold text-amber-800">Save Your Backup Recovery Codes</p>
                <p className="text-xs text-amber-700 mt-0.5">If you lose your authenticator app, use these one-time codes to log in. Each code can only be used once. Store them securely.</p>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                {backupCodes.map((c, i) => (
                  <code key={i} className="font-mono text-sm text-center py-1.5 bg-white rounded border border-gray-200">{c}</code>
                ))}
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(backupCodes.join('\n'));
                  toast.success('Backup codes copied to clipboard');
                }}
                data-testid="copy-backup-codes"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy All Codes
              </Button>
              <Button onClick={() => { setBackupCodes(null); setStep('idle'); }} className="w-full" data-testid="backup-codes-done">
                I've Saved My Codes
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
